import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import Confirm from "../../../../components/confirm/Confirm";
import { Filters } from "../../../../components/filters/Filters";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import WorkflowDisplay, { createWorklowStep } from "../../../../components/workflow/Workflow";
import useRouting, { Link, useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/UserContext";
import {
    FormField, FormFieldDescriptorInput,
    FormType, GetFormDataDocument, ListRequestTypesDocument,
    useDeleteFormMutation, useGetExternalFormFieldsQuery, useGetFormDataQuery,
    useGetFormQuery, useGetRequestTypesQuery, useSaveFormMutation, useSaveWorkflowMutation, WorkflowStep,
    ActionExecuteStartCondition,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import FormDesigner from "./FormDesigner";
import createFormFields, { ExecutorFields } from "./WorkflowFields";
import Select from "../../../../components/select/Select";
import usePortal from "../../../../contexts/PortalContext";

const EXECUTE_CONDITIONS = [
    {
        value: ActionExecuteStartCondition.AfterStarted,
        label: "Assigned Tickets",
    },
    {
        value: ActionExecuteStartCondition.BeforeOrAfterStarted,
        label: "Any ticket",
    },
];

export function ActionFormSelected({ id }: {id: string}): ReactElement {
    const { Paths, historyPush } = useRouting();
    const { id: portalId } = usePortal();
    const nameRef = React.createRef<HTMLInputElement>();
    const userSession = useCurrentSession();

    const {
        data, error, loading: isLoading,
    } = useGetFormQuery({
        fetchPolicy: "no-cache",
        variables: {
            id: id === "new" ? "urn:fq:form:new" : id,
            hasId: id !== "new",
        },
    });

    const [externalFormId, setExternalFormId] = useState(() => data?.form?.externalFormId);

    const { data: formData, loading: isDataLoading, refetch } = useGetFormDataQuery({});
    const { data: externalData } = useGetExternalFormFieldsQuery({
        variables: {
            id: externalFormId,
        },
        skip: externalFormId == null,
    });
    const [saveForm] = useSaveFormMutation();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [deleteForm] = useDeleteFormMutation();

    const [name, setName] = useState(() => data?.form?.name ?? "");
    const [description, setDescription] = useState(() => data?.form?.description ?? "");
    const [isCreatePrivate, setIsCreatePrivate] = useState(() => data?.form?.isCreatePrivate ?? false);
    const [deflectionIdentity, setDeflectionIdentity] = useState(() => (data?.form?.deflection?.identity
        ?? (data?.form?.deflection?.isEnabled ? portalId : undefined)));
    const [startCondition, setStartCondition] = useState(
        () => data?.form?.actionSettings?.startCondition ?? ActionExecuteStartCondition.AfterStarted,
    );

    const [workflow, setWorkflow] = useState<WorkflowStep[]>(() => data?.form?.workflow?.steps as WorkflowStep[] ?? []);

    const [userGroupIds, setUserGroupIds] = useState(() => data?.form?.userGroupIds as string[]
            ?? userSession.user?.userGroups?.toIdList("id")
            ?? []);

    const [fields, setFields] = useState<FormFieldDescriptorInput[]>(
        () => data?.form?.fields.map(field => removeTypename(field)) ?? [],
    );

    const [displayDelete, setDisplayDelete] = useState(false);

    useEffect(() => {
        if (!data) {
            return;
        }
        setName(data?.form?.name ?? "");
        setDescription(data?.form?.description ?? "");
        setFields(data?.form?.fields.map(field => removeTypename(field)) ?? []);
        setUserGroupIds(data?.form?.userGroupIds ?? []);
        setIsCreatePrivate(data?.form?.isCreatePrivate ?? false);
        setDeflectionIdentity(data?.form?.deflection?.identity
            ?? (data?.form?.deflection?.isEnabled ? portalId : undefined));
        setWorkflow(data.form?.workflow?.steps as WorkflowStep[] ?? []);
        setExternalFormId(data.form?.externalFormId);
        setStartCondition(data.form?.actionSettings?.startCondition ?? ActionExecuteStartCondition.AfterStarted);
    }, [data?.form]);

    if (isLoading || isDataLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!nameRef.current?.checkValidity()) {
            nameRef.current?.reportValidity();
            return;
        }
        (async () => {
            const workflowVersion = !workflow ? undefined : await saveWorkflow({
                variables: {
                    id: data?.form?.workflow?.id,
                    workflow: {
                        name: "Auto generated",
                        description: "Auto generated for form",
                        steps: workflow.map(createWorklowStep),
                    },
                },
            });

            await saveForm({
                refetchQueries: [
                    { query: GetFormDataDocument },
                    { query: ListRequestTypesDocument },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    form: {
                        type: FormType.Action,
                        fields: fields.map(field => ({
                            ...field,
                            __typename: undefined,
                        })),
                        name,
                        description,
                        isCreatePrivate,
                        userGroupIds,
                        workflowId: workflowVersion?.data?.saveWorkflow?.id,
                        deflection: {
                            isEnabled: deflectionIdentity != null,
                            identity: deflectionIdentity,
                        },
                        externalFormId,
                        actionSettings: {
                            startCondition,
                        },

                    },
                },
            });
            historyPush(Paths.ADMINISTRATOR_SETTING, {
                setting: "workflow-actions",
            });
        })();
    };

    const onDelete = async () => {
        await deleteForm({
            refetchQueries: [
                { query: GetFormDataDocument },
            ],
            variables: {
                id,
            },
        });
        historyPush(Paths.ADMINISTRATOR_SETTING, {
            setting: "workflow-actions",
        });
    };

    const fieldMap = formData?.formFields.toIdMap("id") ?? {};

    const formFields = (externalData?.externalFormFields as FormField[]
        ?? fields.mapFilter(row => (row.fieldId ? fieldMap[row.fieldId] : undefined)));

    return (
        <>
            <Confirm confirmText="Delete" onConfirm={onDelete} shouldShow={displayDelete} onHide={() => setDisplayDelete(false)}>
                <div>
                    Are you sure you want to delete the action:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {name}
                    </div>

                </div>
            </Confirm>
            <div>
                <div className="mb-4 d-flex">
                    <div className="col border-end me-3 pe-3">
                        <div className="border p-3 rounded mb-4 bg-white">

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">Name</Form.Label>
                                <Form.Control
                                    ref={nameRef}
                                    type="text"
                                    placeholder="Name"
                                    required
                                    onChange={evt => setName(evt.target.value)}
                                    value={name}
                                />
                                <Form.Text>
                                    The name of the action displayed at the top and in the action selection.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Description
                                    {" "}
                                    <small className="text-light">(Optional)</small>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    as="textarea"
                                    placeholder="This action is used for ..."
                                    onChange={evt => setDescription(evt.target.value)}
                                    value={description}
                                />
                                <Form.Text>
                                    The optional description is to help you understand where this action
                                    is used. It will never be displayed to the end user.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Groups
                                </Form.Label>
                                <UserGroups
                                    isMulti
                                    canCreate
                                    value={userGroupIds}
                                    onSelected={ids => setUserGroupIds(ids)}
                                />
                                <Form.Text>
                                    Which user groups are allowed to execute this action?
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    When to execute
                                </Form.Label>
                                <Select
                                    placeholder="Tickets assigned to you."
                                    onChange={value => setStartCondition(value.value as ActionExecuteStartCondition)}
                                    value={EXECUTE_CONDITIONS.find(condition => condition.value === startCondition)}
                                    options={EXECUTE_CONDITIONS}
                                />
                                <Form.Text>
                                    When can this action be executed? Only after the ticket has assigned, or to any ticket
                                    in any state, before or after assignment?
                                </Form.Text>
                            </Form.Group>

                        </div>

                        <div className="border p-3 rounded mb-4 bg-white">
                            <h5 className="mb-2 text-light">
                                Automations
                            </h5>
                            <div>
                                Automation allows you to create logic, trigger services, and take actions on this requests.
                            </div>

                            <WorkflowDisplay
                                value={workflow}
                                onChange={updated => setWorkflow(updated)}
                                input={createFormFields(formFields, ExecutorFields.CAN_SET)}
                            />

                        </div>

                    </div>
                    <div className="d-flex flex-column col-4 ">
                        <div className="mb-4">
                            <FormDesigner
                                fields={fields}
                                formData={formData}
                                formName={name}
                                groupIds={userGroupIds}
                                id={id}
                                onCustomFieldAdded={refetch}
                                setFields={setFields}
                                requestFormType="action"
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                    {id !== "new" && (
                        <button type="button" className="btn btn-none text-danger me-4" onClick={() => setDisplayDelete(true)}>
                            Delete
                        </button>
                    )}

                    <button type="submit" className="btn btn-primary" onClick={onSubmit}>
                        Save
                    </button>
                </div>

            </div>
        </>
    );
}

export function ActionFormList(): ReactElement {
    const { data, error, loading: isLoading } = useGetRequestTypesQuery({
        fetchPolicy: "no-cache",
        variables: {
            type: FormType.Action,
        },
    });
    const { Paths } = useRouting();
    const userSession = useCurrentSession();

    const [formFilters, setFormFilters] = useState<Set<string>>(() => new Set(userSession.user?.userGroups.toIdList("id")));
    const [formSearch, setFormSearch] = useState<string>("");

    if (isLoading) {
        return <Loading />;
    }
    if (error || !data) {
        return <ErrorRemote error={error} />;
    }

    let { forms } = data;

    if (formFilters.size > 0 && data.userGroups.isNotEmpty) {
        forms = forms?.filter(row => row.userGroupIds?.isEmpty || row.userGroupIds?.some(id => formFilters.has(id)));
    }
    if (formSearch) {
        const search = formSearch.toLocaleLowerCase();
        forms = forms?.filter(row => row.name.toLocaleLowerCase().includes(search)
            || row.description?.toLocaleLowerCase().includes(search));
    }

    return (

        <div className="border p-3 rounded bg-white ms-2">
            {data.forms?.isNotEmpty ? (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="text-light mb-0">
                            Action
                        </h5>
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting: "workflow-actions",
                            }}
                            className="btn btn-none btn-sm"
                        >
                            <AiOutlinePlus />
                            {" "}
                            Action
                        </Link>
                    </div>
                    <Filters
                        categories={data.userGroups}
                        selectedCategories={formFilters}
                        onSelected={setFormFilters}
                        search={formSearch}
                        onSetSearch={setFormSearch}
                    />
                    {forms.isNotEmpty ? (

                        <div className="list-group">
                            {forms?.map(field => (
                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    key={field.id}
                                    params={{
                                        id: field.id,
                                        setting: "workflow-actions",
                                    }}
                                    className="list-group-item list-group-item-action flex-column align-items-start"
                                >
                                    <div className="d-flex w-100 align-items-end mb-2">
                                        <h5 className="mb-0">
                                            {field.name}
                                            {" "}
                                        </h5>

                                        <small className="ms-3 text-light " />
                                    </div>
                                    <small>{field.description}</small>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-light">No actions found matching your filters.</div>
                    )}
                    <div className="d-flex justify-content-end align-items-center mt-3">

                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting: "workflow-actions",
                            }}
                            className="btn btn-secondary btn-sm"
                        >
                            <AiOutlinePlus />
                            {" "}
                            Action
                        </Link>
                    </div>

                </>
            ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                    <h5 className="text-light">
                        Actions
                    </h5>
                    <Link
                        to={Paths.ADMINISTRATOR_SETTING_ID}
                        params={{
                            id: "new",
                            setting: "workflow-actions",
                        }}
                        className="btn btn-secondary btn-sm my-3"
                    >
                        <AiOutlinePlus />
                        {" "}
                        Add Action
                    </Link>
                    <small>
                        Actions allow you to automate your workflow. Actions allow you to invoke a set of commands
                        manually from the Agent interface. Create an action, specify some fields, and kick of a set of
                        actions in a list directly from the Agent interface.
                    </small>
                </div>

            )}

        </div>

    );
}

export default function ActionForm(): ReactElement {
    const { Paths } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    return id ? <ActionFormSelected id={id} /> : <ActionFormList />;
}
